import {Portal} from 'solid-js/web';
import {batch, createContext, createEffect, createRoot, For, onCleanup, onMount, useContext, JSX, createMemo, createSignal, Accessor, untrack, createResource, Resource, on, createReaction, Show, createRenderEffect, createComputed, Setter} from 'solid-js';
import styles from '@components/browser.module.scss';
import {ButtonIconTsx} from '@components/buttonIconTsx';
import getTextWidth from '@helpers/canvas/getTextWidth';
import {FontFull, FontFullBold} from '@config/font';
import {createStore, reconcile, unwrap} from 'solid-js/store';
import untrackActions from '@helpers/solid/untrackActions';
import classNames from '@helpers/string/classNames';
import Scrollable from '@components/scrollable2';
import fastSmoothScroll from '@helpers/fastSmoothScroll';
import {IconTsx} from '@components/iconTsx';
import {ButtonMenuItemOptionsVerifiable, ButtonMenuSync} from '@components/buttonMenu';
import {attachContextMenuListener} from '@helpers/dom/attachContextMenuListener';
import ListenerSetter from '@helpers/listenerSetter';
import findUpClassName from '@helpers/dom/findUpClassName';
import contextMenuController from '@helpers/contextMenuController';
import positionMenu from '@helpers/positionMenu';
import copy from '@helpers/object/copy';
import {filterButtonMenuItems} from '@components/buttonMenuToggle';
import Animated from '@helpers/solid/animations';
import WebApp, {WebAppLaunchOptions} from '@components/webApp';
import deferredPromise from '@helpers/cancellablePromise';
import documentFragmentToNodes from '@helpers/dom/documentFragmentToNodes';
import wrapEmojiText from '@lib/richTextProcessor/wrapEmojiText';
import I18n, {i18n} from '@lib/langPack';
import {avatarNew} from '@components/avatarNew';
import {getMiddleware} from '@helpers/middleware';
import MovablePanel from '@helpers/movablePanel';
import {MovableState} from '@components/movableElement';
import {Ref, resolveElements, resolveFirst} from '@solid-primitives/refs';
import Section from '@components/section';
import InputSearch from '@components/inputSearch';
import rootScope from '@lib/rootScope';
import {SimilarPeer} from '@components/chat/similarChannels';
import SearchIndex from '@lib/searchIndex';
import {useUser} from '@stores/peers';
import {Game, Message, Page, User} from '@layer';
import TelegramWebView from '@components/telegramWebView';
import showForwardPopup from '@components/popups/forward';
import {getOverlayRoot} from '@helpers/appWindow';
import getPeerActiveUsernames from '@appManagers/utils/peers/getPeerActiveUsernames';
import internalLinkProcessor from '@lib/internalLinkProcessor';
import {INTERNAL_LINK_TYPE} from '@lib/internalLink';
import {InstantView} from '@components/instantView';
import {WebPage} from '@layer';
import SolidJSHotReloadGuardProvider from '@lib/solidjs/hotReloadGuardProvider';
import {copyTextToClipboard} from '@helpers/clipboard';
import safeWindowOpen from '@helpers/dom/safeWindowOpen';
import wrapTelegramRichText from '@lib/richTextProcessor/wrapTelegramRichText';
import limitSymbols from '@helpers/string/limitSymbols';
import {toastNew} from '@components/toast';
import pause from '@helpers/schedulers/pause';
import assumeType from '@helpers/assumeType';
import {useAppSettings} from '@stores/appSettings';
import cancelEvent from '@helpers/dom/cancelEvent';
import clamp from '@helpers/number/clamp';
import windowSize from '@helpers/windowSize';
import IS_TOUCH_SUPPORTED from '@environment/touchSupport';

type BrowserPageProps<T = {}> = T & {
  title: string, // plain text
  icon: JSX.Element,
  dispose: () => void,
  titleWidth?: () => number,
  id?: string,
  menuButtons?: ButtonMenuItemOptionsVerifiable[],
  scrollFromPage?: BrowserPageProps<any>,
  content?: JSX.Element | (() => JSX.Element),
  cacheKey?: string,

  isCatalogue?: boolean,

  isConfirmationNeededOnClose?: () => void | Promise<boolean>,
  needBackButton?: boolean,
  onBackClick?: () => void
};

function BrowserHeaderTipSvg(props: {left?: boolean, right?: boolean}) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" class={classNames(styles.BrowserHeaderSelectorTail, props.left ? styles.left : styles.right)}>
      <path fill-rule="evenodd" clip-rule="evenodd" d="M16 16V0C16 8.83656 8.83656 16 0 16H16Z" fill="var(--surface-color)"/>
    </svg>
  );
}

function BrowserHeaderButton(props: Parameters<typeof ButtonIconTsx>[0]) {
  return (
    <ButtonIconTsx {...props} class={classNames(styles.BrowserHeaderButton, props.class)} noRipple />
  );
}

function BrowserHeaderTab(props: {
  page: BrowserPageProps,
  ref: Ref<HTMLDivElement>,
  openPageMenu: (e: MouseEvent | TouchEvent) => void,
  index: Accessor<number>
}) {
  const [state, actions] = useContext(BrowserContext);
  const isActive = createMemo(() => !state.collapsed && state.page === props.page);
  const transform = createMemo(() => {
    if(!state.collapsed) {
      return '';
    }

    const index = props.index();
    let value = -20 + Math.min(2, index) * -26;
    if(index > 2) {
      value += (index - 2) * -40;
    }

    return `translateX(${value}px)`;
  });

  return (
    <div
      ref={props.ref}
      class={classNames(
        styles.BrowserHeaderTab,
        isActive() && styles.active,
        state.pages.indexOf(props.page) === 0 && styles.first
      )}
      style={{
        '--text-width': props.page.titleWidth() + 26 + 'px',
        'z-index': Math.max(1, 4 - props.index()),
        'transform': transform()
      }}
      onClick={() => actions.select(props.page)}
    >
      <BrowserHeaderButton class={styles.BrowserHeaderTabIcon} aria-label={props.page.title}>
        <span class={styles.BrowserHeaderTabIconInner}>{props.page.icon}</span>
        <IconTsx
          icon="more"
          class={classNames(styles.BrowserHeaderTabHover, styles.BrowserHeaderTabMore)}
          onClick={props.openPageMenu}
        />
      </BrowserHeaderButton>
      <div dir="auto" class={styles.BrowserHeaderTabTitle}>
        {documentFragmentToNodes(wrapEmojiText(props.page.title))}
      </div>
      <div class={classNames(styles.BrowserHeaderTabHover, styles.BrowserHeaderTabMask)}></div>
      <ButtonIconTsx
        icon="close"
        class={classNames(styles.BrowserHeaderTabHover, styles.BrowserHeaderTabClose)}
        noRipple
        onClick={(e) => {
          e.stopPropagation();
          actions.close(props.page);
        }}
      />
    </div>
  );
}

function BrowserHeader(props: {
}) {
  const [state, actions] = useContext(BrowserContext);
  const needBackButton = createMemo(() => !!state.page.needBackButton);
  const tabMap: Map<string, HTMLDivElement> = new Map();

  createEffect(() => {
    const page = state.page;
    const scrollFromPage = state.page?.scrollFromPage;
    if(!scrollFromPage) {
      return;
    }

    fastSmoothScroll({
      container: scrollableRef,
      element: tabMap.get(page.id),
      position: 'center',
      axis: 'x',
      forceDuration: 200,
      getNormalSize: ({rect}) => {
        const diff = scrollFromPage.titleWidth() - page.titleWidth();
        return rect.width + diff/*  + page.titleWidth */;
      }
    });
  });

  const openPageMenu = async(e: MouseEvent | TouchEvent) => {
    const target = findUpClassName(e.target, styles.BrowserHeaderTab);
    if(!target) {
      return;
    }

    if(!('touches' in e)) e.preventDefault(); // cross-realm-safe mouse check (Document PiP window)
    // smth
    if(!('touches' in e)) e.cancelBubble = true;

    const page = state.pages.find((page) => tabMap.get(page.id) === target);
    if(!page?.menuButtons) {
      return;
    }

    const listenerSetter = new ListenerSetter();
    const copied = page.menuButtons.map((button) => (button = unwrap(button), button.element ? button : copy(button)));
    const buttons = (await filterButtonMenuItems(copied)).map((button) => {
      button.options = {listenerSetter};
      return button;
    });
    const element = ButtonMenuSync({
      buttons,
      listenerSetter
    });
    element.classList.add('contextmenu');

    getOverlayRoot().append(element);

    positionMenu(e, element);
    contextMenuController.openBtnMenu(element, () => {
      setTimeout(() => {
        element.remove();
        listenerSetter.removeAll();
      }, 1e3);
    });
  };

  onMount(() => {
    const listenerSetter = new ListenerSetter();
    attachContextMenuListener({
      element: scrollableRef,
      callback: openPageMenu,
      listenerSetter
    });

    onCleanup(() => {
      listenerSetter.removeAll();
    });
  });

  const collapsedTitle = createMemo(() => {
    const wrapTitles = (pages: BrowserPageProps[]) => pages.map((page) => wrapEmojiText(page.title));
    const pages = state.pages;
    if(pages.length === 1) {
      return i18n('MiniApps.Collapsed.One', wrapTitles([pages[0]]));
    } else if(pages.length === 2) {
      return i18n('MiniApps.Collapsed.Two', wrapTitles(pages.slice(0, 2)));
    } else {
      return i18n('MiniApps.Collapsed.Many', [pages.length - 1, ...wrapTitles([pages[0]])]);
    }
  });

  let scrollableRef: HTMLDivElement;
  return (
    <div class={styles.BrowserHeader}>
      <BrowserHeaderButton
        aria-label={I18n.format(needBackButton() ? 'AccDescr.Back' : 'Close', true)}
        onClick={() => {
          if(needBackButton()) {
            state.page.onBackClick();
          } else {
            actions.close(state.page);
          }
        }}
      >
        <div class={classNames('animated-close-icon', needBackButton() && 'state-back')}></div>
      </BrowserHeaderButton>
      <Scrollable
        class={classNames(styles.BrowserHeaderTabsScrollable, state.collapsed && 'disable-hover')}
        axis="x"
        ref={scrollableRef}
        withBorders="manual"
      >
        <div class={styles.BrowserHeaderTabs}>
          <div
            class={styles.BrowserHeaderSelector}
            style={{
              transform: `translateX(${state.index * 40 + 7 + (state.index >= 1 ? 16 : 0)}px)`,
              width: state.page.titleWidth() + 16 * 2 + 34 + 'px'
            }}
          >
            <BrowserHeaderTipSvg left />
            <BrowserHeaderTipSvg right />
          </div>
          <Animated type="grow-width" mode="add-remove">
            <For each={state.pages}>{(page, index) => {
              return (
                <BrowserHeaderTab
                  page={page}
                  openPageMenu={openPageMenu}
                  ref={(el) => tabMap.set(page.id, el)}
                  index={index}
                />
              );
            }}
            </For>
          </Animated>
          <BrowserHeaderButton
            class={classNames(styles.BrowserHeaderTabIcon, styles.BrowserHeaderNewButton)}
            aria-label={I18n.format('MiniApps.OpenApp', true)}
            onClick={() => openCatalogueInAppBrowser()}
          >
            <span class={styles.BrowserHeaderTabIconInner}><IconTsx icon="plus" /></span>
          </BrowserHeaderButton>
        </div>
      </Scrollable>
      <div
        class={styles.BrowserHeaderCollapsedTitle}
        style={{'--translateX': Math.max(0, 3 - state.pages.length) * -14 + 'px'}}
      >
        {collapsedTitle()}
      </div>
      <Show when={state.canCollapse}>
        <BrowserHeaderButton
          icon={state.collapsed ? 'app_expand' : 'app_shrink'}
          aria-label={I18n.format(state.collapsed ? 'InAppBrowser.Restore' : 'InAppBrowser.Minimize', true)}
          onClick={() => actions.toggleCollapsed()}
        />
      </Show>
    </div>
  );
}

type BrowserContextState = {
  pages: BrowserPageProps[],
  index: number,
  page: BrowserPageProps,
  destroyed: boolean,
  collapsed: boolean,
  canCollapse: boolean
};
type BrowserContextActions = {
  add: (page: BrowserPageProps) => void,
  select: (page: BrowserPageProps | number) => void,
  close: (page: BrowserPageProps) => void,
  destroy: () => void,
  toggleCollapsed: (collapse?: boolean) => void,
  replace: (page: BrowserPageProps, originalPage: BrowserPageProps) => void,
  setCanCollapse: (canCollapse: boolean) => void
};

export type BrowserContextValue = [
  state: BrowserContextState,
  actions: BrowserContextActions
];

function createBrowserStore(props: {
  pages: BrowserPageProps[]
}): BrowserContextValue {
  const initialState: BrowserContextState = {
    pages: [],
    index: 0,
    get page() {
      return state.pages[state.index];
    },
    destroyed: false,
    collapsed: false,
    canCollapse: true
  };

  const [state, setState] = createStore<BrowserContextState>(initialState);

  const actions: BrowserContextActions = untrackActions({
    add: (page) => {
      page = makeBrowserPage(page);
      setState('pages', (pages) => {
        return [...pages, page];
      });
      actions.select(state.pages.length - 1);
    },
    select: (page) => {
      let newIndex: number;
      if(typeof(page) === 'number') {
        if(page < 0 || page >= state.pages.length) {
          newIndex = -1;
        } else {
          newIndex = page;
        }
      } else {
        newIndex = state.pages.indexOf(page);
      }

      if(newIndex === -1) {
        return;
      }

      batch(() => {
        const currentPage = state.page;
        const newPage = state.pages[newIndex];
        setState('index', newIndex);
        setState('pages', newIndex, 'scrollFromPage', currentPage);

        queueMicrotask(() => {
          const index = state.pages.indexOf(newPage);
          setState('pages', index, 'scrollFromPage', undefined);
        });
      });
    },
    close: async(page) => {
      const index = state.pages.findIndex((_page) => _page.id === page.id);
      if(index === -1) {
        return;
      }

      await page.isConfirmationNeededOnClose?.();

      if(state.pages.length === 1) {
        actions.destroy();
        return;
      }

      const pages = state.pages.slice();
      pages.splice(index, 1);

      let newIndex: number;
      if(index < state.index) {
        newIndex = state.index - 1;
      } else if(index === state.index) {
        newIndex = Math.min(state.index, pages.length - 1);
      }

      setState({
        pages,
        ...(newIndex !== undefined && {index: newIndex})
      });

      page.dispose();
    },
    destroy: () => {
      if(state.destroyed) {
        return;
      }

      setState('destroyed', true);
    },
    toggleCollapsed: (collapse) => {
      if(!untrack(() => state.canCollapse)) {
        collapse = false;
      }

      setState('collapsed', collapse ?? ((v) => !v));
    },
    replace: (page, originalPage) => {
      page.id = originalPage.id;
      page = makeBrowserPage(page);
      originalPage.dispose();
      setState('pages', state.pages.findIndex((_page) => _page.id === originalPage.id), reconcile(page));
    },
    setCanCollapse: (canCollapse) => {
      setState({
        canCollapse,
        ...(!canCollapse && {collapsed: false})
      });
    }
  });

  props.pages.forEach(actions.add);
  setState('index', 0);

  onCleanup(() => {
    state.pages.forEach((page) => page.dispose());
  });

  return [state, actions];
}

const BrowserContext = createContext<BrowserContextValue>();
function Browser(props: {
  onExit: () => void
}) {
  const width = 480;
  const height = 640;
  const aspectRatio = width / height;
  const additionalHeight = 48;
  const minWidth = 328;
  const minHeight = height * (minWidth / width) + additionalHeight;

  const [state, actions] = lastContext = useContext(BrowserContext);
  const [movableState, setMovableState] = createSignal<MovableState>({
    width,
    height: height + additionalHeight
  });

  onMount(() => {
    const listenerSetter = new ListenerSetter();
    const movablePanel = new MovablePanel({
      listenerSetter,
      movableOptions: {
        minWidth,
        minHeight,
        element: ref,
        verifyTouchTarget: (e, type) => {
          const target = e.target;
          if(type === 'move' && (
            !findUpClassName(target, styles.BrowserHeader) ||
            findUpClassName(target, styles.BrowserHeaderButton) ||
            findUpClassName(target, styles.BrowserHeaderTab)
          )) {
            return false;
          }

          return true;
        },
        aspectRatio: IS_TOUCH_SUPPORTED ? aspectRatio : undefined,
        resetTransition: true
      },
      onResize: (movableState) => {
        if(state.collapsed) {
          return;
        }

        setMovableState(movableState);
      },
      previousState: movableState()
    });

    createEffect(() => {
      const {movable} = movablePanel;
      if(!movable) {
        createEffect(() => {
          setMovableState({
            width: windowSize.width - 16,
            height: Math.min(688, windowSize.height - 16 * 2)
          });

          ref.style.width = movableState().width + 'px';
          ref.style.height = movableState().height + 'px';
        });

        actions.setCanCollapse(false);
        ref.classList.add(styles.noMovable);
        onCleanup(() => {
          actions.setCanCollapse(true);
          ref.classList.remove(styles.noMovable);
        });
        return;
      }

      const {collapsed} = state;
      const collapsedWidth = 328;
      const collapsedHeight = 48;
      movable.toggleResizable(!collapsed);
      movable.setMinValues(
        collapsed ? collapsedWidth : minWidth,
        collapsed ? collapsedHeight : minHeight
      );

      const previousMovableState = untrack(movableState);
      movable.state = collapsed ? {
        ...previousMovableState,
        width: collapsedWidth,
        height: collapsedHeight
      } : {
        ...movable.state,
        width: previousMovableState.width,
        height: previousMovableState.height
      };
    });

    onCleanup(() => {
      listenerSetter.removeAll();
      movablePanel.destroy();
    });
  });

  let ref: HTMLDivElement;
  const main = (
    <div ref={ref} class={classNames(styles.Browser, state.collapsed && styles.collapsed, 'movable-element')}>
      <BrowserHeader />
      <div
        class={styles.BrowserBody}
        style={{
          'width': movableState().width + 'px',
          '--browser-width': movableState().width + 'px',
          'height': movableState().height - additionalHeight + 'px'
        }}
      >
        <For each={state.pages}>{(page) => {
          return (
            <div
              class={classNames(
                styles.BrowserPage,
                state.page !== page && 'hide'
              )}
            >
              {typeof(page.content) === 'function' ? page.content() : page.content}
            </div>
          );
        }}</For>
      </div>
    </div>
  );

  const animated = (
    <Animated type="cross-fade" mode="add-remove" appear>
      {!state.destroyed && main}
    </Animated>
  );

  const child = resolveFirst(() => animated);
  createEffect(() => {
    if(!child()) {
      props.onExit();
    }
  });

  return (
    <Portal>
      {animated}
    </Portal>
  );
}

function makeBrowserPage(props: BrowserPageProps): BrowserPageProps {
  props.titleWidth ??= () => getTextWidth(props.title, FontFullBold);
  props.id ??= Math.random().toString(36).slice(2);
  props.menuButtons ??= [];
  props.menuButtons.push({
    icon: 'close',
    text: 'Close',
    onClick: () => {
      lastContext[1].close(props);
    }
  });
  return props;
}

let lastContext: BrowserContextValue;
export function openInAppBrowser(page?: BrowserPageProps) {
  if(lastContext) {
    lastContext[1].add(page);
    lastContext[1].toggleCollapsed(false);
    return;
  }

  createRoot((dispose) => {
    const pages: BrowserPageProps[] = [page];

    const store = createBrowserStore({
      pages: pages.filter(Boolean)
    });

    return (
      <BrowserContext.Provider value={store}>
        <Browser
          onExit={() => {
            lastContext = undefined;
            dispose();
          }}
        />
      </BrowserContext.Provider>
    );
  });
}

/** Takes the in-app browser down with every page in it, the way its own close button does. */
export function closeInAppBrowser() {
  lastContext?.[1].destroy();
}

export async function openWebAppInAppBrowser(options: WebAppLaunchOptions) {
  if(lastContext && options.cacheKey) {
    const page = lastContext[0].pages.find((page) => page.cacheKey === options.cacheKey);
    if(page) {
      lastContext[1].select(page);
      return;
    }
  }

  const deferred = deferredPromise<void>();
  const [needBackButton, setNeedBackButton] = createSignal(false);
  const [destroy, setDestroy] = createSignal(false);
  const webApp = new WebApp({
    ...options,
    header: document.createElement('div'),
    title: document.createElement('div'),
    body: document.createElement('div'),
    forceHide: () => setDestroy(true),
    onBackStatus: setNeedBackButton
  });

  const title = await webApp.getTitle(true);
  if(destroy()) {
    webApp.destroy();
    options.onClose?.();
    return;
  }

  webApp.init(() => deferred);

  const middlewareHelper = getMiddleware();
  const avatar = avatarNew({
    peerId: webApp.getPeerId(),
    size: 24,
    middleware: middlewareHelper.get()
  });
  await avatar.readyThumbPromise;
  if(destroy()) {
    middlewareHelper.destroy();
    webApp.destroy();
    options.onClose?.();
    return;
  }

  return createRoot((dispose) => {
    const initialState: BrowserPageProps = {
      title,
      icon: avatar.node,
      menuButtons: webApp.getMenuButtons(),
      dispose,
      isConfirmationNeededOnClose: webApp.isConfirmationNeededOnClose,
      content: webApp.body,
      get needBackButton() {
        return needBackButton();
      },
      onBackClick: webApp.onBackClick,
      cacheKey: webApp.cacheKey
    };

    onCleanup(() => {
      webApp.destroy();
      options.onClose?.();
    });

    createEffect(() => {
      if(destroy()) {
        lastContext[1].close(initialState);
      }
    });

    queueMicrotask(() => deferred.resolve());
    // const [state, setState] = createStore<BrowserPageProps>(initialState);
    const lastState = lastContext?.[0];
    if(lastState && lastState?.page.isCatalogue) {
      lastContext[1].replace(initialState, lastContext[0].page);
    } else {
      openInAppBrowser(initialState);
    }

    createEffect(on(() => lastContext[0].collapsed, (collapsed) => {
      webApp.notifyVisible(!collapsed);
    }))
  });
}

const GAME_SANDBOX_ATTRIBUTES = [
  'allow-scripts',
  'allow-same-origin',
  'allow-popups',
  'allow-popups-to-escape-sandbox',
  'allow-forms',
  'allow-modals',
  'allow-orientation-lock',
  'allow-pointer-lock'
].join(' ');

const GAME_ALLOW_ATTRIBUTES = 'accelerometer; gyroscope; magnetometer; gamepad; fullscreen; autoplay; clipboard-write;';

export async function openGameInAppBrowser(options: {
  game: Game.game,
  message: Message.message,
  url: string
}) {
  const {game, message, url} = options;
  const cacheKey = `game-${game.id}-${message.peerId}-${message.mid}`;

  if(lastContext) {
    const existing = lastContext[0].pages.find((page) => page.cacheKey === cacheKey);
    if(existing) {
      lastContext[1].select(existing);
      lastContext[1].toggleCollapsed(false);
      return;
    }
  }

  const shareMessage = async() => {
    const mids = await rootScope.managers.appMessagesManager.getMidsByMessage(message);
    showForwardPopup({[message.peerId]: mids});
  };

  const body = document.createElement('div');
  body.classList.add(styles.BrowserGameBody);

  const telegramWebView = new TelegramWebView({
    url,
    sandbox: GAME_SANDBOX_ATTRIBUTES,
    allow: GAME_ALLOW_ATTRIBUTES,
    onLoad: () => {
      telegramWebView.iframe.style.opacity = '1';
    }
  });

  const iframe = telegramWebView.iframe;
  iframe.classList.add(styles.BrowserGameIframe);
  iframe.style.opacity = '0';
  body.append(iframe);

  telegramWebView.addEventListener('share_game', shareMessage);
  telegramWebView.addEventListener('share_score', shareMessage);

  const botId = message.viaBotId || message.fromId;
  const middlewareHelper = getMiddleware();
  const avatar = avatarNew({
    peerId: botId ? (botId as UserId).toPeerId(false) : message.peerId,
    size: 24,
    middleware: middlewareHelper.get()
  });
  await avatar.readyThumbPromise;

  return createRoot((dispose) => {
    const initialState: BrowserPageProps = {
      title: game.title || '',
      icon: avatar.node,
      menuButtons: [{
        icon: 'forward',
        text: 'ShareFile',
        onClick: shareMessage,
        verify: () => true
      }],
      dispose,
      content: body,
      cacheKey
    };

    onCleanup(() => {
      telegramWebView.destroy();
      middlewareHelper.destroy();
    });

    queueMicrotask(() => telegramWebView.onMount());

    const lastState = lastContext?.[0];
    if(lastState && lastState?.page.isCatalogue) {
      lastContext[1].replace(initialState, lastContext[0].page);
    } else {
      openInAppBrowser(initialState);
    }
  });
}

export async function openCatalogueInAppBrowser() {
  return createRoot((dispose) => {
    const [folded, setFolded] = createSignal(true);
    const [value, setValue] = createSignal('');
    const targets = new WeakMap<HTMLElement, BotId>;
    const searchIndex = new SearchIndex<BotId>({ignoreCase: true});

    const inputSearch = new InputSearch({
      placeholder: 'MiniApps.Search',
      onChange: (value) => {
        value = value.trim();
        setValue(value);
      },
      debounceTime: 200,
      oldStyle: true
    });

    inputSearch.container.classList.add(styles.BrowserCatalogueSearch);

    const render = (botIds: BotId[], ready: () => void) => {
      const promises: Promise<any>[] = [];
      const ret = botIds.map((botId) => {
        return (
          <SimilarPeer
            ref={(el) => targets.set(el, botId)}
            peerId={botId.toPeerId(false)}
            promises={promises}
            avatarSize={64}
          />);
      });

      Promise.all(promises).then(ready);
      return ret;
    };

    const children = (items: Accessor<JSX.Element>, folded?: Accessor<boolean>) => {
      return (
        <div class={classNames(styles.BrowserCatalogueList, folded?.() && styles.folded)}>{items()}</div>
      );
    };

    const processBotIds = (botIds: BotId[]) => {
      untrack(() => {
        botIds.forEach((botId) => {
          const user = useUser(botId) as User.user;
          searchIndex.indexObjectArray(botId, [user.first_name, user.last_name, ...getPeerActiveUsernames(user)].filter(Boolean));
        });
      });

      return botIds;
    };

    const search = (
      <Section name="MiniApps.AppsSearch" noShadow>
        <Loader
          loader={() => {
            const query = value();
            return async() => {
              const result = searchIndex.search(query);
              return [...result];
            };
          }}
          render={render}
        >
          {children}
        </Loader>
      </Section>
    );

    const main = (
      <div>
        <Loader
          loader={() => {
            return async() => {
              const result = await rootScope.managers.appUsersManager.getTopPeers('bots_app');
              return processBotIds(result.map((user) => user.id.toPeerId(false)));
            };
          }}
          render={render}
        >
          {(items) => {
            const elements = resolveElements(items).toArray;
            const isMoreThanEnough = createMemo(() => elements().length > 7);
            if(!isMoreThanEnough()) {
              setFolded(false);
            }

            return (
              <Section
                name="MiniApps.Apps"
                nameRight={isMoreThanEnough() && (
                  <span onClick={() => setFolded((folded) => !folded)}>
                    {i18n(folded() ? 'MiniApps.AppsMore' : 'MiniApps.AppsLess')}
                  </span>
                )}
                noShadow
              >
                {children(items, folded)}
              </Section>
            );
          }}
        </Loader>
        <Loader
          loader={() => {
            let offset = '';
            return async() => {
              const result = await rootScope.managers.appAttachMenuBotsManager.getPopularAppBots(offset, 50);
              offset = result.nextOffset;
              return processBotIds(result.userIds);
            };
          }}
          render={render}
        >
          {(items) => {
            return items() && (
              <Section name="MiniApps.Popular" noShadow>
                {children(items)}
              </Section>
            );
          }}
        </Loader>
      </div>
    );

    const initialState: BrowserPageProps = {
      title: 'Open App',
      icon: IconTsx({icon: 'plus'}),
      dispose,
      content: (
        <Scrollable>
          <div
            class={styles.BrowserCatalogue}
            onClick={(e) => {
              const target = findUpClassName(e.target as HTMLElement, 'similar-channels-channel');
              if(!target) {
                return;
              }

              const botId = targets.get(target);
              internalLinkProcessor.processWebAppLink({
                _: INTERNAL_LINK_TYPE.WEB_APP,
                appname: '',
                domain: getPeerActiveUsernames(useUser(botId) as User.user)[0]
              });
              // lastContext[1].close(initialState);
            }}
          >
            {inputSearch.container}
            <Animated type="cross-fade">
              {value() ? search : main}
            </Animated>
          </div>
        </Scrollable>
      ),
      isCatalogue: true
    };

    openInAppBrowser(initialState);
    // queueMicrotask(() => deferred.resolve());
  });
}

function Loader<T>(props: {
  loader: () => () => Promise<T>,
  render: (item: T, ready: () => void) => JSX.Element,
  children: (items: Accessor<JSX.Element>) => JSX.Element,
  onReady?: () => void
}) {
  const loader = createMemo(props.loader);
  const [ready, setReady] = createSignal(false);
  const [rendered, setRendered] = createSignal<JSX.Element[]>(undefined, {equals: false});

  const loadMore = (first?: boolean) => {
    const _loader = loader();
    const [result, setResult] = createSignal<T>();
    _loader().then(setResult);

    createReaction(() => {
      const onReady = () => {
        setRendered((v) => {
          if(first) {
            return [rendered];
          }

          return [...v, rendered];
        });

        props.onReady?.();
        setReady(true);
      };

      let returned = false;
      const rendered = props.render(result(), () => {
        if(returned) {
          onReady();
        } else {
          returned = true;
        }
      });

      if(returned) {
        onReady();
      } else {
        returned = true;
      }
    })(result);
  };

  createEffect(on(loader, () => loadMore(true)));

  return (
    <>
      {ready() && props.children(rendered)}
    </>
  );
}

type InstantViewPageExtraProps = {
  url: string,
  setAnchor: Setter<string>
};

export function openInstantViewInAppBrowser({
  webPageId: _webPageId,
  cachedPage,
  HotReloadGuardProvider,
  anchor
}: {
  webPageId?: Long,
  cachedPage?: Page | string,
  HotReloadGuardProvider: typeof SolidJSHotReloadGuardProvider,
  anchor?: string // * expect it to be '#name'
}) {
  if(!cachedPage && anchor) {
    (lastContext[0].page as BrowserPageProps<InstantViewPageExtraProps>).setAnchor(anchor);
    return;
  }

  const TEST_PART = false;
  let hadCachedPage = typeof(cachedPage) !== 'string';
  let url: string;
  if(hadCachedPage) {
    assumeType<Page>(cachedPage);
    url = cachedPage.url;
    if(TEST_PART) {
      cachedPage.blocks = cachedPage.blocks.slice(0, -5);
    }

    // * preload page first because anchor can be missing
    if(anchor && cachedPage.pFlags.part) {
      hadCachedPage = false;
      cachedPage = undefined;
    }
  } else {
    url = cachedPage as string;
    cachedPage = undefined;
    anchor = new URL(url).hash;

    if(/* anchor &&  */lastContext && lastContext[0].page) {
      const page = lastContext[0].page as BrowserPageProps<InstantViewPageExtraProps>;
      const u1 = new URL(page.url);
      const u2 = new URL(url);
      u2.hash = u1.hash = '';
      // * protocol can be http or https
      if(u1.hostname === u2.hostname && u1.pathname === u2.pathname) {
        page.setAnchor(anchor);
        return;
      }
    }
  }

  assumeType<Page>(cachedPage);

  const [webPageId, setWebPageId] = createSignal<Long>(_webPageId);
  const [page, setPageStore] = createStore<Page>(undefined);
  const [pageResource] = createResource<Page>(async() => {
    if(
      !TEST_PART &&
      hadCachedPage &&
      (!cachedPage.pFlags.part && cachedPage.views !== undefined)
    ) {
      return cachedPage;
    }

    if(TEST_PART) await pause(10000);
    return rootScope.managers.appWebPagesManager.getWebPage(url)
    .then((webPage) => {
      setWebPageId(webPage.id);
      return (webPage as WebPage.webPage).cached_page;
    });
  }, {initialValue: cachedPage});

  const needFadeIn = !hadCachedPage;
  createRoot((dispose) => {
    createComputed(() => {
      if(pageResource()) {
        setPageStore(reconcile(pageResource()));
      }
    });

    const [_anchor, setAnchor] = createSignal(anchor, {equals: false});
    const [appSettings, setAppSettings] = useAppSettings();

    const waitForReady = !lastContext && hadCachedPage;
    let wasReady = false;
    const onReady = () => {
      if(wasReady) {
        return;
      }

      wasReady = true;
      openInAppBrowser(initialState);
    };

    const scaleModifier = 0.1;
    const changeScale = (add: boolean, e: MouseEvent) => {
      cancelEvent(e);
      setAppSettings(
        'instantView',
        'scale',
        (value) => clamp(+(value + (add ? scaleModifier : -scaleModifier)).toFixed(2), 0.5, 1.5)
      );
      return false;
    };

    const initialState: BrowserPageProps<InstantViewPageExtraProps> = {
      get title() {
        if(pageResource.loading && !pageResource.latest) {
          return I18n.format('Loading', true);
        }

        const block = page.blocks.find((block) => block._ === 'pageBlockTitle');
        if(!block) {
          return /* webPage().site_name ||  */'Instant View';
        }

        const textWithEntities = wrapTelegramRichText(block.text);
        return limitSymbols(textWithEntities.text, 20);
      },
      menuButtons: [{
        element: (
          <span class={classNames('btn-menu-item', styles.ScaleMenu)}>
            <span class={classNames(styles.ScaleMenuSmaller, 'hover-effect')} onClick={changeScale.bind(null, false)}>A</span>
            <span class={styles.ScaleMenuScale}>{Math.round(appSettings.instantView.scale * 100) + '%'}</span>
            <span class={classNames(styles.ScaleMenuBigger, 'hover-effect')} onClick={changeScale.bind(null, true)}>A</span>
          </span>
        ) as HTMLElement,
        onClick: () => {}
      }, {
        icon: 'newtab',
        text: 'OpenInNewTab',
        onClick: () => safeWindowOpen(url),
        separator: true,
        verify: () => !!url // * markdown IV has no real URL — hide open/copy-link actions
      }, {
        icon: 'copy',
        text: 'CopyLink',
        onClick: () => {
          copyTextToClipboard(url);
          toastNew({langPackKey: 'LinkCopied'});
        },
        verify: () => !!url
      }],
      icon: <IconTsx icon="boostcircle_filled" />,
      dispose,
      content: (
        <Show when={pageResource()}>
          <HotReloadGuardProvider>
            <InstantView
              webPageId={webPageId()}
              page={page}
              openNewPage={(url) => {
                openInstantViewInAppBrowser({cachedPage: url, HotReloadGuardProvider});
              }}
              anchor={_anchor()}
              needFadeIn={needFadeIn}
              collapse={() => {
                lastContext[1].toggleCollapsed(true);
              }}
              onReady={waitForReady ? () => queueMicrotask(onReady) : undefined}
            />
          </HotReloadGuardProvider>
        </Show>
      ),

      url,
      setAnchor
    };

    if(!waitForReady) {
      onReady();
    }
  });
}
